<template>
  <div class="mt-4 overflow-hidden">
    <div :class="table({ class: 'grid-cols-[repeat(3,minmax(max-content,1fr))]' })">
      <SortableTableHeader v-model:sort-column="sortColumn" v-model:sort-direction="sortDirection">
        <SortableTableHeaderCell column="booking">{{ t('booking') }}</SortableTableHeaderCell>
        <SortableTableHeaderCell column="customer">{{ t('customer') }}</SortableTableHeaderCell>
        <SortableTableHeaderCell column="downpaymentAmount" numeric>
          {{ t('downpaymentAmount') }}
        </SortableTableHeaderCell>
      </SortableTableHeader>
      <div v-for="(item, index) in sortedItems" :key="rowKey(item, index)" :class="tableRow()">
        <div :class="tableCell()">
          <router-link
            v-if="item.bookingId"
            :to="{ name: 'bookingEdit', params: { id: item.bookingId } }"
            class="text-link"
          >
            {{ item.bookingNumber ?? '-' }}
          </router-link>
          <span v-else>{{ t('noBooking') }}</span>
        </div>
        <div :class="tableCell()">
          {{ item.customerName ?? '-' }}
        </div>
        <div :class="tableCell({ numeric: true })">
          {{ formatCurrency(item.downpaymentAmount) }}
        </div>
      </div>
    </div>
    <div class="px-4">
      <EntityListExceptions
        :status="downpaymentsStatus"
        :error="downpaymentsError"
        :items-count="sortedItems.length"
        :no-items-text="t('noDownpayments')"
        small
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import SortableTableHeader from '@/components/SortableTableHeader.vue';
import SortableTableHeaderCell from '@/components/SortableTableHeaderCell.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { table, tableCell, tableRow } from '@carvia/theme/lib/cva/grid';
import type {
  ReceivablesDownpaymentItemDto,
  ReceivablesDownpaymentsDto,
} from '@carvia/ros-client-types';

const props = defineProps<{
  downpayments?: ReceivablesDownpaymentsDto;
  downpaymentsStatus: 'success' | 'pending' | 'error';
  downpaymentsError: unknown;
}>();

const { t } = useI18n();

const SORT_COLUMNS = ['booking', 'customer', 'downpaymentAmount'] as const;
type SortColumn = (typeof SORT_COLUMNS)[number];

const sortColumn = useRouteQueryRef('sort', '', 'string');
const sortDirection = useRouteQueryRef<'asc' | 'desc'>('sortDir', 'asc', 'string');

const sortValue = (
  item: ReceivablesDownpaymentItemDto,
  column: SortColumn,
): string | number | null => {
  switch (column) {
    case 'booking':
      return item.bookingNumber ?? null;
    case 'customer':
      return item.customerName ?? null;
    case 'downpaymentAmount':
      return item.downpaymentAmount;
  }
};

const sortedItems = computed(() => {
  const items = [...(props.downpayments?.items ?? [])];
  const column = sortColumn.value;
  if (!(SORT_COLUMNS as readonly string[]).includes(column)) {
    return items.sort((left, right) => right.downpaymentAmount - left.downpaymentAmount);
  }
  const direction = sortDirection.value === 'desc' ? -1 : 1;
  return items.sort((left, right) => {
    const a = sortValue(left, column as SortColumn);
    const b = sortValue(right, column as SortColumn);
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * direction;
  });
});

const rowKey = (item: ReceivablesDownpaymentItemDto, index: number) =>
  item.bookingId ?? `catch-all-${index}`;
</script>

<i18n lang="json">
{
  "en": {
    "booking": "Booking",
    "customer": "Customer",
    "downpaymentAmount": "Downpayment amount",
    "noBooking": "No booking",
    "noDownpayments": "No downpayments"
  },
  "de": {
    "booking": "Buchung",
    "customer": "Kunde",
    "downpaymentAmount": "Anzahlung",
    "noBooking": "Keine Buchung",
    "noDownpayments": "Keine Anzahlungen"
  },
  "es": {
    "booking": "Reserva",
    "customer": "Cliente",
    "downpaymentAmount": "Anticipo",
    "noBooking": "Sin reserva",
    "noDownpayments": "Sin anticipos"
  }
}
</i18n>
